/**
 * Конфигурация тарифов
 */

export interface TariffFeature {
    label: string; // Ключ для перевода
    value?: string | number; // Для числовых значений (например, "5", "15", "Unlimited")
}

export interface Tariff {
    id: number;
    name: string;
    price: number;
    price_with_vat: number;
    features: TariffFeature[];
}

export const TARIFFS: Tariff[] = [
    {
        id: 4954,
        name: 'Virtual Office Lite',
        price: 69,
        price_with_vat: 69,
        features: [
            { label: 'tariff.legalAddress' },
            { label: 'tariff.receptionMails' },
            { label: 'tariff.coffeeSoftDrinks' },
            { label: 'tariff.access24_7' },
            { label: 'tariff.sharedFaxLine' },
            { label: 'tariff.mailScanning' },
            { label: 'tariff.freePages', value: 5 },
            { label: 'tariff.personalBox' },
        ],
    },
    {
        id: 5940,
        name: 'Virtual Office Lite (discounted)',
        price: 69,
        price_with_vat: 69,
        features: [
            { label: 'tariff.legalAddress' },
            { label: 'tariff.receptionMails' },
            { label: 'tariff.coffeeSoftDrinks' },
            { label: 'tariff.access24_7' },
            { label: 'tariff.sharedFaxLine' },
            { label: 'tariff.mailScanning' },
            { label: 'tariff.freePages', value: 5 },
            { label: 'tariff.personalBox' },
        ],
    },
    {
        id: 4843,
        name: 'Virtual Office',
        price: 99,
        price_with_vat: 99,
        features: [
            { label: 'tariff.legalAddress' },
            { label: 'tariff.receptionMails' },
            { label: 'tariff.coffeeSoftDrinks' },
            { label: 'tariff.access24_7' },
            { label: 'tariff.sharedFaxLine' },
            { label: 'tariff.mailScanning' },
            { label: 'tariff.freePages', value: 15 },
            { label: 'tariff.personalBox' },
            { label: 'tariff.ipTelephony' },
            { label: 'tariff.freeMeetingHours', value: 2 },
            { label: 'tariff.freeParcels', value: 1 },
            { label: 'tariff.discountLevel', value: '10%' },
        ],
    },
    {
        id: 4844,
        name: 'Virtual Office +',
        price: 119,
        price_with_vat: 119,
        features: [
            { label: 'tariff.legalAddress' },
            { label: 'tariff.receptionMails' },
            { label: 'tariff.coffeeSoftDrinks' },
            { label: 'tariff.access24_7' },
            { label: 'tariff.sharedFaxLine' },
            { label: 'tariff.mailScanning' },
            { label: 'tariff.freePages', value: 'Unlimited' },
            { label: 'tariff.personalBox' },
            { label: 'tariff.freeMeetingHours', value: 4 },
            { label: 'tariff.freeParcels', value: 3 },
            { label: 'tariff.discountLevel', value: '20%' },
            { label: 'tariff.brusselsPhoneLine' },
            { label: 'tariff.phoneCallsManagement' },
            { label: 'tariff.ipTelephony' },
        ],
    },
];

export const INITIAL_TARIFF: Tariff = {
    id: 0,
    name: '',
    price: 0,
    price_with_vat: 0,
    features: [],
};

export const PUBLIC_TARIFFS: Tariff[] = TARIFFS.filter((tariff) => tariff.id !== 5940);

/** Значение по умолчанию для селекта тарифа в регистрации (id из API `subscription`) */
export function resolvePublicTariffId(subscriptionFromApi?: number | null): {
    id: string;
    isDefaultValue: boolean;
} {
    const firstId = String(PUBLIC_TARIFFS[0]?.id ?? TARIFFS[0].id);
    if (subscriptionFromApi == null || subscriptionFromApi === 0) {
        return { id: firstId, isDefaultValue: true };
    }
    const n = Number(subscriptionFromApi);
    if (Number.isNaN(n) || !PUBLIC_TARIFFS.some((tariff) => tariff.id === n)) {
        return { id: firstId, isDefaultValue: true };
    }
    return { id: String(n), isDefaultValue: false };
}

/**
 * Получить тариф по ID
 */
export const getTariffById = (id: number): Tariff | undefined => {
    return TARIFFS.find((tariff) => tariff.id === id);
};

/**
 * Получить тарифы дороже указанного
 */
export const getTariffsMoreExpensiveThan = (currentTariffId: number): Tariff[] => {
    const currentTariff = getTariffById(currentTariffId);
    if (!currentTariff) {
        return PUBLIC_TARIFFS;
    }
    return PUBLIC_TARIFFS.filter((tariff) => tariff.price > currentTariff.price);
};

/**
 * Получить только новые фичи тарифа, которых нет в предыдущем тарифе или которые отличаются по значению
 */
export const getNewFeatures = (tariff: Tariff, previousTariff: Tariff | undefined): TariffFeature[] => {
    if (!previousTariff) {
        return tariff.features;
    }

    // Создаем Map для быстрого поиска фичей предыдущего тарифа по label
    const previousFeaturesMap = new Map<string, TariffFeature>();
    previousTariff.features.forEach((f) => {
        previousFeaturesMap.set(f.label, f);
    });

    // Возвращаем фичи, которые:
    // 1. Отсутствуют в предыдущем тарифе (новые фичи)
    // 2. Имеют другой value по сравнению с предыдущим тарифом (измененные значения)
    return tariff.features.filter((feature) => {
        const previousFeature = previousFeaturesMap.get(feature.label);

        // Если фичи нет в предыдущем тарифе - это новая фича
        if (!previousFeature) {
            return true;
        }

        // Если фича есть, но значение отличается - это изменение значения
        // Сравниваем значения, учитывая что они могут быть разных типов
        const currentValue = feature.value;
        const previousValue = previousFeature.value;

        // Если оба undefined - фича одинаковая, не показываем
        if (currentValue === undefined && previousValue === undefined) {
            return false;
        }

        // Если значения отличаются - показываем
        return currentValue !== previousValue;
    });
};
